export type Json = Record<string, unknown>;
let csrf = "";
export const setCsrf = (value: string) => {
  csrf = value;
};

export async function api<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    cache: "no-store",
    ...(body === undefined
      ? {}
      : {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
          body: JSON.stringify(body),
        }),
  }).catch(() => {
    throw new Error(
      "Unable to reach Tedalytics. Check your connection and try again.",
    );
  });
  if (!response.ok) {
    const data = (await response
      .json()
      .catch(() => ({ detail: "Connection failed" }))) as { detail?: string };
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : `Please check the entered values (${response.status}).`,
    );
  }
  return response.json() as Promise<T>;
}

const decode = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), (c) =>
    c.charCodeAt(0),
  );
const encode = (b: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

interface PublicOptions {
  challenge_id: string;
  options: Record<string, unknown>;
}
export async function passkey(
  register: boolean,
  token = "",
  name = "My passkey",
) {
  const action = register ? "registration" : "login";
  const data = await api<PublicOptions>(
    `/auth/${action}/options`,
    register ? { token } : {},
  );
  const o = data.options;
  const options = {
    ...o,
    challenge: decode(o.challenge as string),
  } as unknown as PublicKeyCredentialCreationOptions &
    PublicKeyCredentialRequestOptions;
  if (register) {
    const user = o.user as { id: string; name: string; displayName: string };
    options.user = { ...user, id: decode(user.id) };
    options.excludeCredentials = (
      (o.excludeCredentials || []) as { id: string; type: "public-key" }[]
    ).map((c) => ({ ...c, id: decode(c.id) }));
  }
  const credential = (await (register
    ? navigator.credentials.create({ publicKey: options })
    : navigator.credentials.get({
        publicKey: options,
      }))) as PublicKeyCredential | null;
  if (!credential) throw new Error("Passkey operation was cancelled");
  const r = credential.response;
  const response: Record<string, unknown> = {
    clientDataJSON: encode(r.clientDataJSON),
  };
  if (r instanceof AuthenticatorAttestationResponse) {
    response.attestationObject = encode(r.attestationObject);
    response.transports = r.getTransports();
  } else {
    const assertion = r as AuthenticatorAssertionResponse;
    response.authenticatorData = encode(assertion.authenticatorData);
    response.signature = encode(assertion.signature);
    if (assertion.userHandle)
      response.userHandle = encode(assertion.userHandle);
  }
  return api<{ authenticated: boolean; csrf: string }>(
    `/auth/${action}/verify`,
    {
      challenge_id: data.challenge_id,
      name,
      credential: {
        id: credential.id,
        rawId: encode(credential.rawId),
        type: credential.type,
        response,
        clientExtensionResults: credential.getClientExtensionResults(),
      },
    },
  );
}
